import React from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Link,
} from "@mui/material";
import { format } from "date-fns-tz";
import { useNavigate, Link as RouterLink } from "react-router-dom";

export const buildValueText = (value, type) => {
  let valueText;
  switch (type) {
    case "smallText":
    case "largeText":
      valueText = value || "<unanswered>";
      break;
    case "checkbox":
      valueText = value ? "true" : "false";
      break;
    case "multiSelectDropdown":
    case "multiSelectRadio":
      valueText = value?.name || "<unanswered>";
      break;
    case "multiSelectCheckbox":
      if (value && Object.keys(value).length > 0) {
        const values = Object.keys(value)
          .map((i) => value[i].name)
          .join(", ");
        valueText = `(${values})`;
      } else {
        valueText = "<none>";
      }
      break;
    default:
      valueText = "<unknown>";
  }

  return valueText;
};

const getOrderIdFromTicketOrder = (ticketOrder, orderId) => {
  if (!ticketOrder) return false;
  if (typeof ticketOrder === "string") return ticketOrder.endsWith(`/orders/${orderId}`);
  if (ticketOrder.id === orderId) return true;
  if (ticketOrder.path && ticketOrder.path.endsWith(`/orders/${orderId}`)) return true;
  return false;
};

const getCancellationSummary = (order) => {
  const instances = order?.cancellation?.instances ?? [];
  let totalRefundedUsdCents = 0;
  const refundedTicketIds = [];
  const cancelledTicketIds = [];
  instances.forEach((inst) => {
    const tickets = inst.tickets ?? inst.ticketIds ?? [];
    const ticketIds = Array.isArray(tickets)
      ? tickets.map((t) => (typeof t === "string" ? t : t?.ticketId)).filter(Boolean)
      : [];
    ticketIds.forEach((tid) => cancelledTicketIds.push(tid));
    const instRefund = inst.refundTotalUsdCents ?? 0;
    if (instRefund > 0) {
      totalRefundedUsdCents += instRefund;
      ticketIds.forEach((tid) => refundedTicketIds.push(tid));
    }
  });
  const totalTicketsCancelled = cancelledTicketIds.length;
  const totalTicketsRefunded = refundedTicketIds.length;
  return {
    totalRefundedUsdCents,
    totalTicketsRefunded,
    totalTicketsCancelled,
    refundedTicketIds,
    cancelledTicketIds,
  };
};

const getCountByTicketTypeKey = (ticketIds, ticketsForOrder) => {
  const ticketIdToKey = {};
  (ticketsForOrder || []).forEach((t) => {
    if (t.id && t.ticketTypeKey != null) ticketIdToKey[t.id] = t.ticketTypeKey;
  });
  const countByKey = {};
  ticketIds.forEach((tid) => {
    const key = ticketIdToKey[tid];
    if (key != null) countByKey[key] = (countByKey[key] || 0) + 1;
  });
  return countByKey;
};

const attendeeIdFromEmail = (email) =>
  typeof email === "string" ? email.trim().toLowerCase() : "";

const OrdersList = ({
  orders,
  eventId,
  tickets: eventTickets,
  getOrderDetailsUrl,
  organizationId,
}) => {
  const navigate = useNavigate();

  const orderItems = (orders || []).map((order) => {
    const {
      id,
      eventName,
      orderFormFields,
      totalUsdCents,
      subtotalUsdCents,
      promoCodeDiscountUsdCents,
      items,
      createdAt,
      promoCode,
      affiliate,
      fees,
      totalFeesUsdCents,
      sessionId,
      sessionName,
      event,
    } = order;
      // Get eventId from prop, or extract from order's event reference
      let orderEventId = eventId;
      if (!orderEventId && event) {
        // event is a Firestore document reference
        if (typeof event === 'string') {
          // If it's a path string like "events/eventId"
          orderEventId = event.split('/')[1];
        } else if (event.id) {
          // If it's a resolved document reference
          orderEventId = event.id;
        } else if (event.path) {
          // If it's a document reference with path
          orderEventId = event.path.split('/')[1];
        }
      }
      let USDollar = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      });
      const totalDollars = (totalUsdCents || 0) / 100;
      const subtotalDollars = (subtotalUsdCents || 0) / 100;
      const promoCodeDiscountDollars = (promoCodeDiscountUsdCents || 0) / 100;
      const totalFeesDollars = (totalFeesUsdCents || 0) / 100;
      const totalText = USDollar.format(totalDollars);
      const subtotalText = USDollar.format(subtotalDollars);
      const discountText = USDollar.format(promoCodeDiscountDollars);
      const totalFeesText = USDollar.format(totalFeesDollars);

      const {
        totalRefundedUsdCents,
        totalTicketsCancelled,
        cancelledTicketIds,
      } = getCancellationSummary(order);
      const ticketsForOrder =
        eventTickets && orderEventId
          ? eventTickets.filter((t) => getOrderIdFromTicketOrder(t.order, id))
          : [];
      const cancelledCountByTicketTypeKey =
        cancelledTicketIds.length > 0 && ticketsForOrder.length > 0
          ? getCountByTicketTypeKey(cancelledTicketIds, ticketsForOrder)
          : {};
      const refundedText =
        totalTicketsCancelled > 0
          ? USDollar.format(totalRefundedUsdCents / 100)
          : null;

      const datePattern = "M/dd/yyyy, h:mm a";
      const dateText = format(createdAt.toDate(), datePattern);
      const orderDetailsUrl = getOrderDetailsUrl?.(order);
      const Wrapper = orderDetailsUrl
        ? ({ children }) => (
            <Box
              onClick={() => navigate(orderDetailsUrl)}
              sx={{
                textDecoration: "none",
                color: "inherit",
                cursor: "pointer",
              }}
            >
              {children}
            </Box>
          )
        : ({ children }) => <>{children}</>;
      return (
        <React.Fragment key={`order-${id}`}>
        <Wrapper>
        <Paper
          elevation={5}
          sx={{
            ...(orderDetailsUrl ? { cursor: "pointer" } : {}),
            marginBottom: 3,
          }}
        >
          <Box
            sx={{
              pt: 2.5,
              px: 2.5,
              pb: 2,
            }}
          >
            <Grid container spacing={1}>
              <Grid item md={10}>
                {orderEventId && !eventId ? (
                  <Typography variant="h6" flexGrow={"1"} sx={{ fontWeight: "bold" }}>
                    <Link
                      component="button"
                      variant="inherit"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        navigate(`/events/${orderEventId}/dashboard`);
                      }}
                      sx={{
                        fontWeight: "bold",
                        textDecoration: "none",
                        "&:hover": { textDecoration: "underline" },
                        color: "inherit",
                      }}
                    >
                      {eventName}
                    </Link>
                  </Typography>
                ) : (
                  <Typography
                    variant="h6"
                    flexGrow={"1"}
                    sx={{ fontWeight: "bold" }}
                  >
                    {eventName}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                  {id}
                </Typography>
                {order.cancellation?.status === "full" && (
                  <Typography variant="body2" color="error">
                    Cancelled
                  </Typography>
                )}
                {order.cancellation?.status === "partial" && (
                  <Typography variant="body2" sx={{ color: "warning.main" }}>
                    Partially cancelled
                  </Typography>
                )}
                {sessionName && sessionId && orderEventId && (
                  <Typography variant="subtitle2" color="primary.lightText">
                    <Link
                      component="button"
                      variant="body2"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/events/${orderEventId}/sessions/${sessionId}`);
                      }}
                      sx={{
                        textDecoration: "underline",
                        cursor: "pointer",
                        color: "primary.main",
                      }}
                    >
                      {sessionName}
                    </Link>
                  </Typography>
                )}
                {Boolean(promoCode) && (
                  <Typography>{`Promo Code: ${promoCode}`}</Typography>
                )}
                {Boolean(affiliate) && (
                  <Typography>{`Affiliate: ${affiliate}`}</Typography>
                )}
              </Grid>
              <Grid item md={2} alignItems={"right"}>
                <Box
                  display={"flex"}
                  flexDirection={"column"}
                  alignItems={"right"}
                >
                  <Box
                    display={"flex"}
                    alignItems={"center"}
                    gap={"5px"}
                    marginLeft={"auto"}
                  >
                    <Typography variant="sub2" textAlign={"right"}>
                      {Boolean(subtotalUsdCents) &&
                      subtotalDollars !== totalDollars
                        ? "Subtotal:"
                        : "Total:"}
                    </Typography>
                    <Typography variant="h6" textAlign={"right"}>
                      {Boolean(subtotalUsdCents) ? subtotalText : totalText}
                    </Typography>
                  </Box>
                  {Boolean(subtotalUsdCents) &&
                    subtotalDollars !== totalDollars && (
                      <>
                        <Box
                          display={"flex"}
                          alignItems={"center"}
                          gap={"5px"}
                          marginLeft={"auto"}
                        >
                          <Typography variant="sub2" textAlign={"right"}>
                            Discounts:
                          </Typography>
                          <Typography variant="h6" textAlign={"right"}>
                            {discountText}
                          </Typography>
                        </Box>
                        {Boolean(totalFeesUsdCents) && totalFeesDollars > 0 && (
                          <Box
                            display={"flex"}
                            alignItems={"center"}
                            gap={"5px"}
                            marginLeft={"auto"}
                          >
                            <Typography variant="sub2" textAlign={"right"}>
                              Organizer Fees:
                            </Typography>
                            <Typography variant="h6" textAlign={"right"}>
                              {totalFeesText}
                            </Typography>
                          </Box>
                        )}
                        <Box
                          display={"flex"}
                          alignItems={"center"}
                          gap={"5px"}
                          marginLeft={"auto"}
                        >
                          <Typography variant="sub2" textAlign={"right"}>
                            Total:
                          </Typography>
                          <Typography variant="h6" textAlign={"right"}>
                            {totalText}
                          </Typography>
                        </Box>
                      </>
                    )}
                  <Typography marginLeft={"auto"}>{dateText}</Typography>
                  {refundedText && (
                    <Typography
                      variant="body2"
                      marginLeft={"auto"}
                      color="error"
                      sx={{ mt: 0.5 }}
                    >
                      Refunded: {refundedText}
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>

            <Box marginTop={"10px"}>
              {(orderFormFields || []).map(({ name, value, type, key: fieldKey }, index) => {
                const valueText = buildValueText(value, type);
                const isEmailLink =
                  fieldKey === "email" &&
                  organizationId &&
                  typeof value === "string" &&
                  value.trim();
                return (
                  <Box
                    key={`order-${id}-field-${fieldKey}-${index}`}
                    display={"flex"}
                    gap={"20px"}
                  >
                    <Typography sx={{ fontWeight: "bold" }}>{name}</Typography>
                    <Typography>
                      {isEmailLink ? (
                        <Link
                          component={RouterLink}
                          to={`/organizations/${organizationId}/attendees/${attendeeIdFromEmail(value)}`}
                          onClick={(e) => e.stopPropagation()}
                          sx={{
                            color: "primary.main",
                            "&:hover": { textDecoration: "underline" },
                          }}
                        >
                          {valueText}
                        </Link>
                      ) : (
                        valueText
                      )}
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            {/* Add fees section */}
            {fees && fees.length > 0 && (
              <Box marginTop={"10px"}>
                <Typography sx={{ fontWeight: "bold", marginBottom: "5px" }}>
                  Fees
                </Typography>
                {fees.map((fee, index) => (
                  <Box
                    key={`order-${id}-fee-${index}`}
                    display={"flex"}
                    gap={"20px"}
                  >
                    <Typography>{fee.name}</Typography>
                    <Typography>{USDollar.format(fee.amount / 100)}</Typography>
                  </Box>
                ))}
              </Box>
            )}

            <Box sx={{ marginTop: "10px", marginBottom: 1 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ marginBottom: 0.5 }}>
                Tickets
              </Typography>
              {(items || []).map(({ name, quantity, ticketTypeKey }) => {
                const cancelledForItem =
                  cancelledCountByTicketTypeKey[ticketTypeKey] || 0;
                return (
                  <Typography
                    key={`order-${id}-item-${ticketTypeKey}`}
                    variant="body2"
                  >
                    {quantity} × {name}
                    {cancelledForItem > 0 && (
                      <Box
                        component="span"
                        sx={{ color: "error.main", marginLeft: 0.5 }}
                      >
                        ({cancelledForItem} cancelled)
                      </Box>
                    )}
                  </Typography>
                );
              })}
            </Box>
          </Box>
        </Paper>
        </Wrapper>
        </React.Fragment>
      );
  });
  return <>{orderItems}</>;
};

export default OrdersList;
